import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Platform, View } from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { SITE_ROOT, APP } from '../../../config';
import { useIsMobile } from '../../../hooks';
import { toSlugCase, unSlugify } from '../../../modules/stringUtils';
import { addQueryStringParams } from '../../../modules/url';
import { QueryFilter } from '../search/QueryFilter';
import { LocationFilter } from '../search/LocationFilter';
import SearchInputModalTrigger from '../search/SearchPopover/SearchInputModalTrigger';
import { SEARCH_PARAM_RESET_CODE } from '../../../modules/consumer/search/Constants';
import { SEARCH_ROUTE } from '../../../route-names';

import {
  BodyText,
  Button,
  HeaderText,
  Stack,
} from '../../ui';
import { ArrowRightCircleIcon, BasicSearchIcon } from '../../ui/Icons';

import { SEARCH_SOURCE } from './constants';

import './HomeHero.module.scss';

/**
 * Renders the home page search hero.
 * @example
 * import HomeHero from 'components/consumer/HomeHero';
 *
 * const MyComponent = () => {
 *   return (
 *     <HomeHero />
 *   );
 * }
 *
 * @param query The current search query
 * @param date The search date currently set in the search criteria
 * @param latitude The latitude currently set in the search criteria
 * @param longitude The longitude currently set in the search criteria
 * @param address The address currently set in the search criteria
 * @param onPopoverOpen Called when one of the components in the home hero expands
 * to full screen
 * full screen
 * @param timeSlots
 * @param utmParams
 * @param linkTo
 */
export function HomeHeroBase({
  query,
  date,
  latitude,
  longitude,
  address,
  timeSlots,
  utmParams,
  linkTo,
  locationCreated,
}) {
  const locationRef = useRef(null);
  const [searchParams, setSearchParams] = useState({ q: null });
  const isMobile = useIsMobile();
  const [location, setLocation] = useState({
    address,
    latitude,
    longitude,
    created: locationCreated,
  });
  const linkParams = useMemo(() => {
    const { q } = searchParams;
    return {
      q: toSlugCase(q || query),
      date,
      loc: toSlugCase(location?.address),
      lat: location.latitude ?? SEARCH_PARAM_RESET_CODE,
      lon: location.longitude ?? SEARCH_PARAM_RESET_CODE,
      timeSlots,
      source: SEARCH_SOURCE,
      placeId: location.placeId ?? SEARCH_PARAM_RESET_CODE,
      ...utmParams,
    };
  }, [
    timeSlots,
    location,
    searchParams,
    date,
    query,
  ]);

  const handleShortSubmitClick = () => linkTo(Platform.OS === 'web' ? SEARCH_ROUTE : null, linkParams);

  /**
   * Updates the Search Query
   * if "doExecuteSearch" is true then the user is taken to the search page
   * @returns {void|any}
   * @param qry
   * @param canSubmitQuery
   */
  const updateQuery = (qry, canSubmitQuery = true) => {
    const newSearchParams = {
      ...linkParams,
      q: qry,
    };

    if (canSubmitQuery && location?.address) {
      return linkTo(SEARCH_ROUTE, newSearchParams);
    }

    return setSearchParams({ ...newSearchParams });
  };

  const handleSearchPopoverClose = qry => {
    updateQuery(qry, false);
  };

  const handleQueryBlur = () => {
    if (locationRef.current && !location?.address) {
      locationRef.current.input.focus();
    }
  };

  const goToJoinPage = () => {
    window.location.assign(addQueryStringParams(`${SITE_ROOT}/join`, {
      source: 'pro_hero_block',
      ...utmParams,
    }));
  };

  const { q } = searchParams;
  const displayQ = unSlugify(q);
  const mobileLayout = APP || isMobile;
  const showSearchPopoverTrigger = mobileLayout;

  return (
    <View
      className="client-app"
      styleName="home-hero-component"
    >
      <View />
      <Stack styleName="home-hero-wrapper">
        <Box paddingBottom={16}>
          <HeaderText
            level={1}
            textAlign="left"
          >
            Discover &amp; book local beauty professionals
          </HeaderText>
        </Box>
        <View>
          {showSearchPopoverTrigger ? (
            <SearchInputModalTrigger source="home_hero" />
          ) : (
            <Inline alignItems="center" space={8}>
              <QueryFilter
                setQuery={updateQuery}
                onClosePopover={handleSearchPopoverClose}
                query={displayQ || ''}
                showSearchButton={false}
                onBlur={handleQueryBlur}
              />
              <LocationFilter
                ref={locationRef}
                address={location.address || ''}
                updateLocation={setLocation}
              />
              <Button
                size="large"
                appearance="primary"
                onClick={handleShortSubmitClick}
                testID="search"
                style={{
                  display: 'flex',
                  padding: 8,
                  borderRadius: 4,
                }}
              >
                <BasicSearchIcon color={BrandColor.WhiteWash} />
              </Button>
            </Inline>
          )}
        </View>
      </Stack>
      {(!mobileLayout) && (
        <Button
          onClick={goToJoinPage}
          appearance="silent"
          testID="joinAsProButton"
        >
          <Inline alignItems="center" space={4}>
            <ArrowRightCircleIcon color={BrandColor.FloraDark} />
            <BodyText color={BrandColor.FloraDark} weight="bold">
              Are you a barber or beauty professional?
            </BodyText>
          </Inline>
        </Button>
      )}
    </View>
  );
}

HomeHeroBase.propTypes = {
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  query: PropTypes.string,
  date: PropTypes.string,
  timeSlots: PropTypes.any,
  address: PropTypes.string,
  utmParams: PropTypes.object,
  linkTo: PropTypes.func.isRequired,
  /** The timestamp of when the geolocation was stored */
  locationCreated: PropTypes.number,
};

HomeHeroBase.defaultProps = {
  query: null,
  date: null,
  latitude: null,
  longitude: null,
  address: null,
  timeSlots: [],
  utmParams: {},
  locationCreated: undefined,
};

const mapStateToProps = ({
  search,
  location,
  route,
}) => ({
  date: search.date,
  query: search.query,
  address: search.address ? search.address : location.address,
  // checking address here rather than lat / lon so that we don't run into any issues where the lat
  // / lon are set but the address isn't
  latitude: search.address ? search.latitude : location.latitude,
  longitude: search.address ? search.longitude : location.longitude,
  timeSlots: search.timeSlots,
  locationCreated: !search.address ? location.created : undefined,
  utmParams: route.params,
});

const mapDispatchToProps = dispatch => ({
  linkTo: (route, params) => dispatch.route.go({
    route,
    params,
    notify: true,
  }),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomeHeroBase);
